import React, { useState, useEffect } from 'react';
import api from '../api';
import './Defaults.css';

const DEBIT_CATEGORIES = [
  { id: 'rent', icon: '🏠', label: 'Rent' },
  { id: 'groceries', icon: '🛒', label: 'Groceries' },
  { id: 'utilities', icon: '⚡', label: 'Utilities' },
  { id: 'metro', icon: '🚇', label: 'Metro' },
  { id: 'health', icon: '💊', label: 'Health' },
  { id: 'dining', icon: '🍔', label: 'Dining Out' },
  { id: 'food', icon: '🍱', label: 'Food' },
  { id: 'shopping', icon: '🛍️', label: 'Shopping' },
  { id: 'entertainment', icon: '🎬', label: 'Entertainment' },
  { id: 'travel', icon: '✈️', label: 'Travel' },
  { id: 'savings', icon: '💰', label: 'Savings' },
  { id: 'investments', icon: '📈', label: 'Investments' },
  { id: 'emi', icon: '🏦', label: 'EMI / Loan' },
  { id: 'other', icon: '📦', label: 'Other' },
];

export default function Defaults({ onClose }) {
  const [dashboards, setDashboards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [splitGroups, setSplitGroups] = useState([]);

  const [defaultDashboard, setDefaultDashboard] = useState('');
  const [defaultAccount, setDefaultAccount] = useState('');
  const [defaultSplit, setDefaultSplit] = useState('');
  const [defaultCategory, setDefaultCategory] = useState('');
  
  const [billingCycleType, setBillingCycleType] = useState('last_day'); // last_friday | last_day | manual
  const [billingCycleDay, setBillingCycleDay] = useState(1);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [settingsResp, dashResp] = await Promise.all([
          api.get('/api/profile/settings'),
          api.get('/api/dashboards')
        ]);

        const settings = settingsResp.data;
        setDefaultDashboard(settings.default_dashboard_id || '');
        setDefaultAccount(settings.default_account_id || '');
        setDefaultSplit(settings.default_split_group_id || '');
        setDefaultCategory(settings.default_category || '');

        setBillingCycleType(settings.billing_cycle_type || 'last_day');
        setBillingCycleDay(settings.billing_cycle_day || 1);

        setDashboards(dashResp.data || []);
      } catch (_) { }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (defaultDashboard) {
      (async () => {
        try {
          const [accResp, splitResp] = await Promise.all([
            api.get(`/api/accounts?dashboard_id=${defaultDashboard}`),
            api.get(`/api/split-groups?dashboard_id=${defaultDashboard}`)
          ]);
          setAccounts(accResp.data || []);
          setSplitGroups(splitResp.data || []);
        } catch (_) {
          setAccounts([]);
          setSplitGroups([]);
        }
      })();
    } else {
      setAccounts([]);
      setSplitGroups([]);
    }
  }, [defaultDashboard]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/profile/settings', {
        default_dashboard_id: defaultDashboard || null,
        default_account_id: defaultAccount || null,
        default_split_group_id: defaultSplit || null,
        default_category: defaultCategory || null,
        billing_cycle_type: billingCycleType,
        billing_cycle_day: parseInt(billingCycleDay) || 1
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) { }
    setSaving(false);
  };

  return (
    <div className="def-overlay" onClick={onClose}>
      <div className="def-panel" onClick={e => e.stopPropagation()}>
        <button className="def-close" onClick={onClose}>✕</button>
        <h2 className="def-title">Transaction Defaults</h2>
        <p className="def-subtitle">Set values that auto-fill when you record a transaction.</p>

        {loading ? (
          <div className="def-loading">Loading preferences...</div>
        ) : (
          <div className="def-form">
            <div className="def-field">
              <label>Default Dashboard</label>
              <select value={defaultDashboard} onChange={e => setDefaultDashboard(e.target.value)}>
                <option value="">None — ask me every time</option>
                {dashboards.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="def-field">
              <label>Default Account / Card</label>
              <select value={defaultAccount} onChange={e => setDefaultAccount(e.target.value)} disabled={!defaultDashboard}>
                <option value="">None</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.account_type})</option>
                ))}
              </select>
            </div>

            <div className="def-field">
              <label>Default Split Group</label>
              <select value={defaultSplit} onChange={e => setDefaultSplit(e.target.value)} disabled={!defaultDashboard}>
                <option value="">None (Personal)</option>
                {splitGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div className="def-field">
              <label>Default Category</label>
              <select value={defaultCategory} onChange={e => setDefaultCategory(e.target.value)}>
                <option value="">None</option>
                {DEBIT_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>

            <div className="def-billing-section">
              <h3 className="def-section-title">Billing Cycle</h3>
              <p className="def-section-subtitle">When does your billing period reset? This affects your monthly spend summaries.</p>
              
              <div className="def-billing-grid">
                <button 
                  type="button"
                  className={`def-billing-tile ${billingCycleType === 'last_friday' ? 'active' : ''}`}
                  onClick={() => setBillingCycleType('last_friday')}
                >
                  <span className="def-billing-icon">📅</span>
                  <div className="def-billing-info">
                    <span className="def-billing-label">Last Friday</span>
                    <span className="def-billing-desc">of the month</span>
                  </div>
                  {billingCycleType === 'last_friday' && <span className="def-billing-check">✓</span>}
                </button>

                <button 
                  type="button"
                  className={`def-billing-tile ${billingCycleType === 'last_day' ? 'active' : ''}`}
                  onClick={() => setBillingCycleType('last_day')}
                >
                  <span className="def-billing-icon">🗓️</span>
                  <div className="def-billing-info">
                    <span className="def-billing-label">Last Day</span>
                    <span className="def-billing-desc">of the month</span>
                  </div>
                  {billingCycleType === 'last_day' && <span className="def-billing-check">✓</span>}
                </button>

                <button 
                  type="button"
                  className={`def-billing-tile ${billingCycleType === 'manual' ? 'active' : ''}`}
                  onClick={() => setBillingCycleType('manual')}
                >
                  <span className="def-billing-icon">✏️</span>
                  <div className="def-billing-info">
                    <span className="def-billing-label">Manual</span>
                    <span className="def-billing-desc">Pick a specific day</span>
                  </div>
                  {billingCycleType === 'manual' && <span className="def-billing-check">✓</span>}
                </button>
              </div>

              {billingCycleType === 'manual' && (
                <div className="def-field def-manual-field">
                  <label>Reset Day (1-31)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="31"
                    value={billingCycleDay} 
                    onChange={e => setBillingCycleDay(e.target.value)} 
                  />
                </div>
              )}
            </div>

            <button className={`def-save${saved ? ' saved' : ''}`} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Defaults'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
